// libs
import React, { memo, useState, useEffect, useMemo, useCallback } from 'react';

// styles
import {
  BannerContainer,
  BannerProductText,
  CTAContainer,
  Logo,
  SecondaryCTA,
  ProgressFill,
  CTAText,
} from './styles';

// constants
import { REDIRECT_LOCATION, TRIGGER_TYPE } from './constants';

// hooks
import { useRemoteConfig } from 'shared/store/remoteConfigs/hooks';

// helpers
import { extractStoreKey, getStoreUrl, stripStoreUrl } from 'shared/helpers/storeUrl';
import { getDeviceType } from 'shared/components/ResponsiveLoader/helpers';
import { RENDER_MODE } from 'shared/constants';
import { getSearchText as _getSearchTerm } from 'shared/store/listingV2/helpers';
import isEmpty from 'lodash/isEmpty';
import unescape from 'lodash/unescape';

// selector
import { useSelector } from 'react-redux';
import { getStoreId } from 'shared/store/app/selectors';
import { getSearchTerm } from 'shared/store/listing/selectors';
import { getAppliedFilters as getAppliedFiltersV1 } from 'shared/store/listing/selectors';
import { getAppliedFilters as getAppliedFiltersV2 } from 'shared/store/listingV2/selectors';

// icons
import { IconName, useStoreIcon } from 'shared/icons';

// components
import SwitchTransitionScreen from './components/SwitchTransitionScreen';

// Keys to ignore when deciding if a user filter is applied
const BANNER_IGNORED_FILTER_KEYS = ['catalog_tag_filter', 'q', 'PageSize', 'sort'];

const useIsUserFilterApplied = () => {
  const appliedFiltersV1 = useSelector(getAppliedFiltersV1);
  const appliedFiltersV2 = useSelector(getAppliedFiltersV2);
  const appliedFilters = isEmpty(appliedFiltersV2) ? appliedFiltersV1 : appliedFiltersV2;

  return Object.keys(appliedFilters || {}).some(
    (key) => !BANNER_IGNORED_FILTER_KEYS.includes(key) && !isEmpty(appliedFilters[key])
  );
};

type IStoreBannerProps = {
  currentStoreProductCount: number;
  redirectStoreProductCount?: number;
  storeToRedirect?: string;
  productName?: string;
  redirectUrl?: string;
  isSearch?: boolean;
  onDismiss?: () => void;
};

type IStoreBannerContentProps = {
  currentStoreProductCount: number;
  redirectStoreProductCount?: number;
  storeToRedirect: string;
  productName?: string;
  redirectUrl?: string;
  onDismiss?: () => void;
  isSearch?: boolean;
};

const CHEVRON_RIGHT_MWEB = '12px';
const CHEVRON_RIGHT_DWEB = '20px';

function StoreBannerContent({
  currentStoreProductCount,
  redirectStoreProductCount,
  storeToRedirect,
  productName: productNameProp,
  redirectUrl,
  onDismiss,
  isSearch,
}: IStoreBannerContentProps) {
  const chevronSize =
    getDeviceType() === RENDER_MODE.DESKTOP ? CHEVRON_RIGHT_DWEB : CHEVRON_RIGHT_MWEB;
  // @ts-ignore: any since store can't be hardcoded as enums
  const banner = useRemoteConfig('storeSwitchBanner')?.[storeToRedirect];
  const ChevronRight = useStoreIcon(IconName.CHEVRON_RIGHT);
  const {
    enabled,
    bannerGradient,
    storeLogoURL,
    someResultsText,
    zeroResultsText,
    ctaBorderRadius,
    ctaColor,
    secondaryCTAcolor,
    color,
    ctaTextColor,
    primaryCtaText,
    secondaryCtaText,
    switchTimer,
    progressFillColor,
    switchWithBannerMweb,
    showOnFilter,
    categoryStoreSwitch,
    visitStoreCtaText,
  } = banner;

  const [sec, setSec] = useState<number>(switchTimer || 5);
  const isFilterApplied = useIsUserFilterApplied();
  const storeId = useSelector(getStoreId);
  const searchTerm1 = useSelector(getSearchTerm); // listing
  const searchTerm2 = useSelector(_getSearchTerm); // listingV2
  const productName = productNameProp || searchTerm1 || searchTerm2;

  const initialTimer = useMemo(() => switchTimer || 5, [switchTimer]);

  // Calculate progress percentage (0-100) based on elapsed time
  const progress = useMemo(() => ((initialTimer - sec) / initialTimer) * 100, [sec, initialTimer]);

  const redirectToStore = useCallback(
    (triggerType: TRIGGER_TYPE, location: REDIRECT_LOCATION) => {
      const navigate = (url: string) => {
        if (triggerType === TRIGGER_TYPE.AUTOMATIC) {
          window.location.replace(url);
        } else {
          window.location.href = url;
        }
      };

      const buildTargetUrl = () => {
        if (redirectUrl) {
          const targetUrl = new URL(redirectUrl, window.location.origin);
          targetUrl.searchParams.delete('f');
          targetUrl.searchParams.delete('isFilteredUrl');
          targetUrl.searchParams.set('transition_trigger_type', triggerType);
          targetUrl.searchParams.set('transition_location', location);
          return targetUrl.pathname + targetUrl.search;
        }
        const currentPath = window.location.pathname + window.location.search;
        const basePath = stripStoreUrl(currentPath, storeId);
        const targetUrl = new URL(getStoreUrl(basePath, storeToRedirect), window.location.origin);
        targetUrl.searchParams.delete('f');
        targetUrl.searchParams.delete('isFilteredUrl');
        targetUrl.searchParams.set('transition_trigger_type', triggerType);
        targetUrl.searchParams.set('transition_location', location);
        return targetUrl.pathname + targetUrl.search;
      };

      const url = buildTargetUrl();
      navigate(url);
    },
    [storeToRedirect, redirectUrl, storeId]
  );

  useEffect(() => {
    if (showOnFilter === false && isFilterApplied) return;

    // SLP and other store has no products
    if (isSearch && !redirectStoreProductCount) return;

    // Banner/Transition screen turned off then auto redirect if current store has 0 product
    if (enabled === false) {
      if (currentStoreProductCount === 0) {
        redirectToStore(TRIGGER_TYPE.AUTOMATIC, REDIRECT_LOCATION.ZERO_RESULTS);
      }
      return;
    }
    // Do not auto redirect in case of some results on current store
    if (currentStoreProductCount !== 0) return;

    let timer = null;
    if (sec > 0) {
      timer = setInterval(() => {
        setSec((prev: number) => prev - 1);
      }, 1000);
    } else {
      redirectToStore(TRIGGER_TYPE.AUTOMATIC, REDIRECT_LOCATION.ZERO_RESULTS);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [
    enabled,
    sec,
    currentStoreProductCount,
    redirectToStore,
    isSearch,
    redirectStoreProductCount,
    showOnFilter,
    isFilterApplied,
  ]);

  const bannerText = useMemo(() => {
    const term = unescape(String(productName ?? ''));
    if (isSearch) {
      const count = String(redirectStoreProductCount || 0);
      const template = currentStoreProductCount === 0 ? zeroResultsText : someResultsText;
      return template.replace('{count}', count).replace('{productName}', term);
    } else {
      // category bannerText has no number
      return categoryStoreSwitch.text.replace('{productName}', term);
    }
  }, [
    productName,
    someResultsText,
    zeroResultsText,
    redirectStoreProductCount,
    currentStoreProductCount,
    categoryStoreSwitch,
    isSearch,
  ]);

  const handleManualRedirect = useCallback(() => {
    const location =
      currentStoreProductCount === 0
        ? REDIRECT_LOCATION.ZERO_RESULTS
        : REDIRECT_LOCATION.MIXED_RESULTS;
    redirectToStore(TRIGGER_TYPE.MANUAL, location);
  }, [currentStoreProductCount, redirectToStore]);

  const handleSecondaryCTAClick = useCallback(() => {
    if (onDismiss) {
      onDismiss();
    } else {
      window.history.back();
    }
  }, [onDismiss]);

  const handleAutoRedirect = useCallback(
    () => redirectToStore(TRIGGER_TYPE.AUTOMATIC, REDIRECT_LOCATION.ZERO_RESULTS),
    [redirectToStore]
  );
  // turn banner off entirely
  if (enabled === false) {
    return null;
  }

  // 1. Filter gate — hide banner and transition screen entirely when filters are applied and config disables it
  if (showOnFilter === false && isFilterApplied) {
    return null;
  }

  if (isSearch && redirectStoreProductCount === 0) {
    return null;
  }
  // 2. Transition screen — no banner UI, just auto-redirect with overlay
  if (
    currentStoreProductCount === 0 &&
    switchWithBannerMweb === false &&
    getDeviceType() !== RENDER_MODE.DESKTOP
  ) {
    return (
      <SwitchTransitionScreen onRedirect={handleAutoRedirect} storeToRedirect={storeToRedirect} />
    );
  }

  return (
    <BannerContainer bannerGradient={bannerGradient}>
      <Logo src={storeLogoURL} />
      <BannerProductText color={color}>
        <span>{bannerText}</span>
      </BannerProductText>
      <CTAContainer
        borderRadius={ctaBorderRadius}
        ctaColor={ctaColor}
        ctaTextColor={ctaTextColor}
        kind="primary"
        size="large"
        shape="default"
        onClick={handleManualRedirect}
      >
        {currentStoreProductCount === 0 && (
          <ProgressFill progress={progress} fillColor={progressFillColor} />
        )}
        <CTAText>
          {currentStoreProductCount > 0 ? visitStoreCtaText : `${primaryCtaText} ${sec}s`}
          {currentStoreProductCount > 0 && <ChevronRight color={ctaTextColor} size={chevronSize} />}
        </CTAText>
      </CTAContainer>
      {currentStoreProductCount === 0 && (
        <SecondaryCTA ctaColor={secondaryCTAcolor} onClick={handleSecondaryCTAClick}>
          {secondaryCtaText}
        </SecondaryCTA>
      )}
    </BannerContainer>
  );
}

function StoreBanner(props: IStoreBannerProps) {
  if (!props.storeToRedirect) return null;
  const storeKey = extractStoreKey(props.storeToRedirect);
  if (!storeKey) return null;
  // if (!props.redirectUrl) return null;
  return <StoreBannerContent {...props} storeToRedirect={storeKey} />;
}

export default memo(StoreBanner);
